import { toast } from 'react-toastify';
import {
  createWebhookOnStripeAndUpdateSecretToSupabase,
  getPartialSecretKey,
  getStripeIntegrationInfo,
  getStripeWebhookInfo,
} from '~/lib/.client/api/stripe';
import { sendAnalyticsEvent } from '~/lib/analytics.client';
import { STRIPE_CHECKOUT_SLUG, STRIPE_WEBHOOK_SLUG, SUPABASE_EDGE_FUNCTIONS_FOLDER } from '~/lib/constants';

import { projectStore } from '~/lib/stores/project';
import { stripeStore } from '~/lib/stores/stripe';
import { supabaseStore } from '~/lib/stores/supabase';
import { workbenchStore } from '~/lib/stores/workbench';

import { AnalyticsAction, AnalyticsTrackEvent } from '~/types/analytics';
import { logger } from '~/utils/logger';
import { waitForFirstValue, waitUntil } from '~/utils/nanostore';
import { extractModeFromStripeSecretKey } from '~/utils/stripe-helpers';
import { addWorkDir } from '~/utils/workDir';

const WEBHOOK_PATH = addWorkDir(`${SUPABASE_EDGE_FUNCTIONS_FOLDER}/${STRIPE_WEBHOOK_SLUG}/index.ts`);
const CHECKOUT_PATH = addWorkDir(`${SUPABASE_EDGE_FUNCTIONS_FOLDER}/${STRIPE_CHECKOUT_SLUG}/index.ts`);

export async function init() {
  try {
    const projectId = await waitForFirstValue(projectStore.projectId);

    // FIXME: if this throw users have to reload the page. Is that okay?
    const existingKey = await getPartialSecretKey(projectId);

    ensureStripeWebhookExists(projectId);

    if (existingKey) {
      stripeStore.partialKey.set(existingKey);

      // make sure we fetch the product selection before marking the project as configured
      await fetchProductSelection(projectId, extractModeFromStripeSecretKey(existingKey));
    }

    // we mark stripe as configured as soon as we have the edge function available
    workbenchStore.edgeFunctionsFiles.subscribe((edgeFunctionsFiles) => {
      stripeStore.hasEdgeFunctions.set(WEBHOOK_PATH in edgeFunctionsFiles && CHECKOUT_PATH in edgeFunctionsFiles);
    });

    stripeStore.partialKey.subscribe((partialKey) => {
      stripeStore.isConfigured.set(partialKey?.length > 0);
    });
  } catch (error) {
    logger.error(error);
  }
}

async function fetchProductSelection(projectId: string, mode: 'test' | 'live' | undefined) {
  if (!mode) {
    return;
  }

  const integrationInfo = await getStripeIntegrationInfo(projectId);

  if (!integrationInfo || !integrationInfo[mode]) {
    return;
  }

  const selectedProducts = integrationInfo[mode].products;

  stripeStore.selectedProducts.set(selectedProducts.map(({ id }) => id));
}

async function ensureStripeWebhookExists(projectId: string) {
  // wait until stripe is configured
  await waitUntil(stripeStore.isConfigured, true);

  // wait until supabase is configured
  const supabaseProject = await waitForFirstValue(supabaseStore.supabaseProject);

  // get the webhook info
  const stripeWebhookInfo = await getStripeWebhookInfo(projectId);

  const webhook = stripeWebhookInfo.find(
    ({ edgeFunctionSlug, supabaseProjectId }) =>
      edgeFunctionSlug === STRIPE_WEBHOOK_SLUG && supabaseProjectId === supabaseProject.id,
  );

  /**
   * If we've never created the webhook, then we wait for the project to be ready
   * and we create the webhook.
   */
  if (!webhook) {
    await waitUntil(workbenchStore.isProcessing, false);

    // create the webhook:
    createWebhook(projectId);
  }
}

async function createWebhook(projectId: string) {
  try {
    await createWebhookOnStripeAndUpdateSecretToSupabase(projectId, STRIPE_WEBHOOK_SLUG);
  } catch (error) {
    sendAnalyticsEvent({
      action: AnalyticsAction.Track,
      payload: {
        event: AnalyticsTrackEvent.StripeWebhookCreationFailed,
        properties: {
          message: error instanceof Error ? error.message : `${error}`,
        },
      },
    });

    toast.error(`Failed to create Webhook on Stripe. Reload the page to try again.`);
  }
}
